import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    IconApps,
    IconChevronRight,
    IconFolder,
    IconMenu2,
    IconPlus,
    IconSearch,
    IconStar,
    IconX
} from '@tabler/icons-react';
import {AppContext} from '../contexts/AppContext';
import {useSidebar} from '../contexts';
import {useAuth} from '../hooks/useAuth';
import {Application} from '../types';
import {appScreenPath} from '../utils/screenPath';
import {decodeJWT} from '../utils/jwt';
import {storage} from '../utils/localStorage';
import {isClientAdmin} from '../utils/roles';
import {HomeWidgets} from '../components/HomeWidgets';
import {useHomeWidgets} from '../hooks/useHomeWidgets';
import namedaysData from '../data/namedays.json';
import '../components/Breadcrumb.css';
import './HomePage.css';

interface ProjectGroup {
    id: string;
    name: string;
    sortOrder: number;
    apps: Application[];
}

interface FavoriteState {
    owner: string | null;
    ids: string[];
}

const EMPTY_APPLICATIONS: Application[] = [];

const normalizeSearch = (value: string) => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('sk');

const appName = (app: Application) => app.displayName || app.name;
const targetScreen = (app: Application) => app.screens?.find(
    screen => String(screen.id) === String(app.defaultScreenId)
)?.id ?? app.screens?.[0]?.id;
const formCount = (app: Application) => app.screens?.reduce(
    (sum, screen) => sum + (screen.createForms?.length || 0) + (screen.updateForms?.length || 0),
    0
) || 0;
const appCountLabel = (count: number) => `${count} ${count === 1 ? 'aplikácia' : count > 1 && count < 5 ? 'aplikácie' : 'aplikácií'}`;

function getGreeting() {
    const hour = new Date().getHours();
    if (hour < 5) return 'Vitajte';
    if (hour < 11) return 'Dobré ráno';
    if (hour < 17) return 'Dobrý deň';
    return 'Dobrý večer';
}

const HomePage: React.FC = () => {
    const {applications: contextApplications} = useContext(AppContext);
    const applications: Application[] = contextApplications || EMPTY_APPLICATIONS;
    const {user} = useAuth();
    const {toggleSidebar} = useSidebar();
    const navigate = useNavigate();
    const {widgets: pinnedWidgets, isLoading: widgetsLoading} = useHomeWidgets();
    const [search, setSearch] = useState('');
    const searchBoxRef = useRef<HTMLDivElement | null>(null);
    const [favoriteState, setFavoriteState] = useState<FavoriteState>({owner: null, ids: []});

    useEffect(() => {
        document.title = user?.tenantName ? `Flowy | ${user.tenantName}` : 'Flowy';
    }, [user?.tenantName]);

    // Browser preferences are scoped to the signed-in user and tenant and survive sign-out.
    const favoriteStorageKey = useMemo(() => {
        if (!user?.email) return null;
        const decoded = decodeJWT(storage.getItem('token') || '');
        const tenant = decoded?.tenant?.id ?? user.tenantName ?? '';
        const owner = decoded?.user?.id ?? user.email;
        return `flowy.home.favorites.v1:${JSON.stringify([String(tenant), String(owner)])}`;
    }, [user]);

    useEffect(() => {
        let ids: string[] = [];
        if (favoriteStorageKey) {
            try {
                const saved: unknown = JSON.parse(window.localStorage.getItem(favoriteStorageKey) || '[]');
                if (Array.isArray(saved)) {
                    ids = Array.from(new Set(saved
                        .filter(id => typeof id === 'string' || typeof id === 'number')
                        .map(String)));
                }
            } catch {
                // Unavailable or malformed browser storage must not block the workspace.
            }
        }
        setFavoriteState({owner: favoriteStorageKey, ids});
    }, [favoriteStorageKey]);

    const favoriteIds = favoriteState.owner === favoriteStorageKey ? favoriteState.ids : [];
    const favoriteApps = favoriteIds
        .map(id => applications.find(app => String(app.id) === id))
        .filter((app): app is Application => Boolean(app));

    const toggleFavorite = (app: Application) => {
        if (!favoriteStorageKey) return;
        const id = String(app.id);
        const nextIds = favoriteIds.includes(id)
            ? favoriteIds.filter(savedId => savedId !== id)
            : [...favoriteIds, id];
        setFavoriteState({owner: favoriteStorageKey, ids: nextIds});
        try {
            window.localStorage.setItem(favoriteStorageKey, JSON.stringify(nextIds));
        } catch {
            // Pinning remains usable for this visit if browser storage is unavailable.
        }
    };

    const projectGroups = useMemo(() => {
        const groups = new Map<string, ProjectGroup>();
        applications.forEach(app => {
            const id = String(app.project?.id ?? 'default');
            if (!groups.has(id)) {
                groups.set(id, {
                    id,
                    name: app.project?.displayName || app.project?.name || 'Ostatné aplikácie',
                    sortOrder: app.project?.sortOrder ?? 0,
                    apps: []
                });
            }
            groups.get(id)!.apps.push(app);
        });
        return Array.from(groups.values())
            .map(group => ({
                ...group,
                apps: [...group.apps].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            }))
            .sort((a, b) => a.sortOrder - b.sortOrder);
    }, [applications]);

    const visibleGroups = useMemo(() => {
        const query = normalizeSearch(search.trim());
        return projectGroups
            .map(group => ({
                ...group,
                apps: group.apps.filter(app => normalizeSearch(
                    `${appName(app)} ${app.description || ''} ${group.name}`
                ).includes(query))
            }))
            .filter(group => group.apps.length > 0);
    }, [projectGroups, search]);

    const searching = search.trim() !== '';

    useEffect(() => {
        if (!searching) return;
        const close = (event: MouseEvent) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) setSearch('');
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [searching]);
    const searchResults = useMemo(
        () => visibleGroups.flatMap(group => group.apps.map(app => ({app, projectName: group.name}))),
        [visibleGroups]
    );
    const today = useMemo(() => {
        const date = new Date();
        return {
            label: date.toLocaleDateString('sk-SK', {weekday: 'long', day: 'numeric', month: 'long'}),
            nameday: (namedaysData as Record<string, Record<string, string>>)[String(date.getMonth() + 1)]?.[String(date.getDate())]
        };
    }, []);

    // Without widgets and favourites the page would be empty: show the whole directory instead
    const showDirectory = !widgetsLoading && pinnedWidgets.length === 0 && favoriteApps.length === 0;

    const openApp = (app: Application) => {
        const screenId = targetScreen(app);
        if (screenId) navigate(appScreenPath(app, screenId));
    };

    return (
        <div className="page-body start-page">
            <div className="v5-topbar">
                <button type="button" className="v5-topbar-toggle" onClick={toggleSidebar} aria-label="Prepnúť menu">
                    <IconMenu2 size={18}/>
                </button>
                <span className="start-topbar-label"><IconApps size={15}/>Prehľad</span>
                {user?.tenantName && <span className="start-topbar-org">{user.tenantName}</span>}
            </div>

            <main className="container-fluid start-content">
                <header className="start-heading">
                    <div className="start-greeting">
                        <h1>{getGreeting()}{user?.firstName && <>, <span>{user.firstName}</span></>}</h1>
                        <p>
                            {today.label}
                            {today.nameday && <> · meniny má <strong>{today.nameday}</strong></>}
                        </p>
                    </div>
                    <div className="start-heading-actions">
                        {isClientAdmin() && (
                            <button type="button" className="start-new-statistic" onClick={() => navigate('/statistics/new')}>
                                <IconPlus size={15}/>Nová štatistika
                            </button>
                        )}
                        <div className="start-search-box" ref={searchBoxRef} onKeyDown={event => {
                            if (event.key === 'Escape') setSearch('');
                        }}>
                            <div className="start-search">
                                <IconSearch size={16}/>
                                <input
                                    type="search"
                                    aria-label="Nájsť aplikáciu"
                                    placeholder="Nájsť aplikáciu…"
                                    value={search}
                                    onChange={event => setSearch(event.target.value)}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter' && searchResults[0]) openApp(searchResults[0].app);
                                    }}
                                />
                                {searching && (
                                    <button type="button" onClick={() => setSearch('')} aria-label="Vymazať vyhľadávanie">
                                        <IconX size={14}/>
                                    </button>
                                )}
                            </div>
                            {searching && (
                                <div className="start-search-results" role="listbox" aria-label="Nájdené aplikácie">
                                    {searchResults.length > 0 ? searchResults.map(({app, projectName}, index) => (
                                        <ApplicationEntry
                                            key={app.id}
                                            app={app}
                                            tone={index % 4}
                                            subtitle={projectName}
                                            pinned={favoriteIds.includes(String(app.id))}
                                            canPin={Boolean(favoriteStorageKey)}
                                            onOpen={() => openApp(app)}
                                            onPin={() => toggleFavorite(app)}
                                        />
                                    )) : (
                                        <p className="start-search-empty">Nenašli sme žiadnu aplikáciu.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {!showDirectory && (
                    <section className="start-favorites" aria-labelledby="favorites-heading">
                        <h2 id="favorites-heading"><IconStar size={15}/>Obľúbené</h2>
                        {favoriteApps.length > 0 ? (
                            <div className="start-favorite-list">
                                {favoriteApps.map((app, index) => (
                                    <div key={app.id} className="start-favorite" data-tone={index % 4}>
                                        <button type="button" className="start-favorite-open" disabled={!targetScreen(app)} onClick={() => openApp(app)}>
                                            <span className="start-mini-icon" aria-hidden="true">{appName(app).charAt(0).toUpperCase()}</span>
                                            <span>{appName(app)}</span>
                                        </button>
                                        <button type="button" className="start-favorite-remove" onClick={() => toggleFavorite(app)}
                                                title="Odobrať z obľúbených" aria-label={`Odobrať ${appName(app)} z obľúbených`}>
                                            <IconX size={12}/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="start-favorites-hint">Aplikácie, ktoré chcete mať poruke, si pripnete hviezdičkou vo vyhľadávaní.</p>
                        )}
                    </section>
                )}

                <HomeWidgets applications={applications}/>

                {showDirectory && (
                    <section className="start-directory" aria-labelledby="applications-heading">
                        <h2 id="applications-heading" className="start-section-title">Aplikácie</h2>
                        {projectGroups.length > 0 ? (
                            <div className="start-project-grid">
                                {projectGroups.map(group => (
                                    <section key={group.id} className="start-project" aria-label={group.name}>
                                        <header className="start-project-head">
                                            <span className="start-project-icon"><IconFolder size={20}/></span>
                                            <div><h3>{group.name}</h3><p>{appCountLabel(group.apps.length)}</p></div>
                                        </header>
                                        <div className="start-app-list">
                                            {group.apps.map((app, index) => (
                                                <ApplicationEntry
                                                    key={app.id}
                                                    app={app}
                                                    tone={index % 4}
                                                    pinned={favoriteIds.includes(String(app.id))}
                                                    canPin={Boolean(favoriteStorageKey)}
                                                    onOpen={() => openApp(app)}
                                                    onPin={() => toggleFavorite(app)}
                                                />
                                            ))}
                                        </div>
                                    </section>
                                ))}
                            </div>
                        ) : (
                            <div className="start-empty">
                                <IconApps size={28}/>
                                <h3>Zatiaľ tu nie sú žiadne aplikácie</h3>
                                <p>Priradené aplikácie sa zobrazia na tomto mieste.</p>
                            </div>
                        )}
                    </section>
                )}
            </main>
        </div>
    );
};

const ApplicationEntry: React.FC<{
    app: Application;
    tone: number;
    /** Replaces the screen/form counts, e.g. the project name in search results. */
    subtitle?: string;
    pinned: boolean;
    canPin: boolean;
    onOpen: () => void;
    onPin: () => void;
}> = ({app, tone, subtitle, pinned, canPin, onOpen, onPin}) => {
    const screens = app.screens?.length || 0;
    const forms = formCount(app);
    const available = Boolean(targetScreen(app));
    const screenLabel = `${screens} ${screens === 1 ? 'obrazovka' : screens > 1 && screens < 5 ? 'obrazovky' : 'obrazoviek'}`;
    const formLabel = forms ? ` · ${forms} ${forms === 1 ? 'formulár' : forms < 5 ? 'formuláre' : 'formulárov'}` : '';

    return (
        <div className={`start-app-row${pinned ? ' is-pinned' : ''}`} data-tone={tone}>
            <button type="button" className="start-app-open" disabled={!available} onClick={onOpen}>
                <span className="start-app-icon" aria-hidden="true">{appName(app).charAt(0).toUpperCase()}</span>
                <span className="start-app-copy">
                    <strong title={appName(app)}>{appName(app)}</strong>
                    <small>{available ? (subtitle || app.description || `${screenLabel}${formLabel}`) : 'Zatiaľ bez obrazoviek'}</small>
                </span>
                <IconChevronRight size={16} className="start-app-arrow"/>
            </button>
            <button
                type="button"
                className="start-pin"
                aria-label={`${pinned ? 'Odopnúť' : 'Pripnúť'} ${appName(app)}`}
                title={pinned ? 'Odobrať z obľúbených' : 'Pridať do obľúbených'}
                aria-pressed={pinned}
                disabled={!canPin || (!available && !pinned)}
                onClick={onPin}
            >
                <IconStar size={15} fill={pinned ? 'currentColor' : 'none'}/>
            </button>
        </div>
    );
};

export default HomePage;
